import { showUserAlert } from "../utils/userErrorMessage";
// src/pages/BusHubNew.jsx
import React, { useEffect, useState } from "react";
import Header from "../Component/Header";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase/client";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

function getStoredEmail() {
  return (
    localStorage.getItem("email") ||
    localStorage.getItem("userEmail") ||
    ""
  ).trim();
}

const KOREAN_DIGITS = {
  공: "0",
  영: "0",
  빵: "0",
  일: "1",
  이: "2",
  삼: "3",
  사: "4",
  오: "5",
  육: "6",
  륙: "6",
  칠: "7",
  팔: "8",
  구: "9",
};

function normalizePhoneCandidates(text = "") {
  return Array.from(text)
    .map((char) => {
      if (/\d/.test(char)) return char;
      if (KOREAN_DIGITS[char]) return KOREAN_DIGITS[char];
      if (/[+\-.\s()[\]]/.test(char)) return char;
      return " ";
    })
    .join("");
}

function containsPhoneNumber(text = "") {
  const normalized = normalizePhoneCandidates(text);
  const phonePattern =
    /(?:^|[^\d])(?:\+?82[\s\-().]*10|010)[\s\-().]*\d{3,4}[\s\-().]*\d{4}(?:$|[^\d])/;

  return phonePattern.test(normalized);
}

export default function BusHubNew() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  

  // 폼 상태
  const [form, setForm] = useState({
    title: "",
    car_count: "",
    content: "",
    start_at: "", // YYYY-MM-DDTHH:MM (datetime-local)
    end_at: "",   // YYYY-MM-DDTHH:MM
    price: "",       // 숫자 문자열
  });

  // 자동 채움 값
  const [author, setAuthor] = useState("");
  const [company, setCompany] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [region, setRegion] = useState("");

  const handleChange = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
  };

  const resolveActor = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const email = (user?.email || getStoredEmail()).trim();

    if (!email && !user?.id) {
      return { email: "", userId: user?.id || null, company: "", companyName: "", region: "" };
    }

    let profileQuery = supabase
      .from("profiles")
      .select("email, company, company_name, region")
      .limit(1);
    profileQuery = email
      ? profileQuery.eq("email", email)
      : profileQuery.eq("id", user.id);

    const { data: rows, error } = await profileQuery;
    const prof = rows?.[0] || null;

    if (error) throw error;

    const resolvedEmail = (email || prof?.email || "").trim();
    if (!resolvedEmail) {
      return { email: "", userId: user?.id || null, company: "", companyName: "", region: "" };
    }

    return {
      email: resolvedEmail,
      userId: user?.id || null,
      company: resolvedEmail,
      companyName: prof?.company_name || localStorage.getItem("companyName") || prof?.company || resolvedEmail,
      region: prof?.region || "",
    };
  };

  // author, region 세팅
  useEffect(() => {
    const init = async () => {
      const actor = await resolveActor();
      setAuthor(actor.email);

      if (!actor.email) {
        showUserAlert("로그인 정보를 확인할 수 없습니다. 다시 로그인해 주세요.");
        navigate("/bus-hub");
        return;
      }

      setCompany(actor.company);
      setCompanyName(actor.companyName);
      setRegion(actor.region);
    };
    init().catch(() => {
      showUserAlert("사용자 정보를 불러오지 못했습니다. 관리자에게 문의하세요.");
      navigate("/bus-hub");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // 로컬 datetime-local -> ISO(UTC)로 변환
  const toISO = (local) => {
    if (!local) return null;
    const d = new Date(local);
    if (isNaN(d.getTime())) return null;
    return d.toISOString(); // 서버엔 UTC로 저장(timestamptz)
  };

  const handleSubmit = async () => {
    if (saving) return;

    // 간단 유효성
    if (!form.title.trim()) return showUserAlert("제목을 입력하세요.");
    if (!form.content.trim()) return showUserAlert("내용을 입력하세요.");
    if (containsPhoneNumber(form.content)) {
      return showUserAlert("전화번호는 들어가서는 안됩니다. 전화번호를 삭제하시고 다시 등록해주세요.");
    }
    if (!form.start_at || !form.end_at) return showUserAlert("시작/종료 일시를 모두 입력하세요.");

    // 시간 유효성(둘 다 비어있거나, 둘 다 채워져 있어야 함)
    const startISO = toISO(form.start_at);
    const endISO = toISO(form.end_at);
    if (!startISO || !endISO) return showUserAlert("일시 형식이 올바르지 않습니다.");
    if (new Date(startISO) >= new Date(endISO)) return showUserAlert("종료 일시는 시작 일시보다 늦어야 합니다.");

    // 금액(선택) 숫자화
    const priceNum = form.price === "" ? null : Number(form.price);
    if (form.price !== "" && (!Number.isFinite(priceNum) || priceNum < 0)) {
      return showUserAlert("금액은 0 이상 숫자로 입력하세요.");
    }

    const carCountNum = form.car_count === "" ? null : Number(form.car_count);
    if (form.car_count !== "" && (!Number.isFinite(carCountNum) || carCountNum < 0))
      return showUserAlert("차량 대수는 0 이상 숫자로 입력하세요.");

    setSaving(true);
    try {
      const actor = await resolveActor();
      if (!actor.email) {
        throw new Error("로그인 정보를 확인할 수 없습니다. 다시 로그인해 주세요.");
      }
      if (!actor.company) {
        throw new Error("회사 정보를 확인할 수 없습니다. 다시 로그인해 주세요.");
      }
      setAuthor(actor.email);
      setCompany(actor.company);
      setCompanyName(actor.companyName);
      setRegion(actor.region);

      // const payload = {
      //   title: form.title.trim(),
      //   car_count: form.car_count === "" ? null : Number(form.car_count) || 0,
      //   content: form.content,
      //   author,          // 로그인 이메일 그대로
      //   region: region || null, // profiles에서 가져온 지역
      //   // created_at, views 등은 DB default가 있으면 생략
      // };
      const payload = {
        title: form.title.trim(),
        car_count: form.car_count === "" ? null : Number(form.car_count) || 0,
        content: form.content,
        start_at: startISO,
        end_at: endISO,
        price: priceNum,
        author: actor.email,
        company: actor.company,
        company_name: actor.companyName || actor.company,
        region: actor.region || null,
        ...(actor.userId ? { author_id: actor.userId } : {}),
      };

      const { error } = await supabase
        .from("bus_hub_posts") // ← DB가 'bus-hub-posts'면 이 이름으로 교체
        .insert([payload]);

      if (error) throw error;

      showUserAlert("게시글이 등록되었습니다.");
      navigate("/bus-hub");
    } catch (e) {
      showUserAlert("등록 실패: " + (e?.message || "알 수 없는 오류"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-1">
      <Header />
      <div className="px-4 pt-1 pb-4 space-y-2">
        <h2 className="text-lg font-semibold">버스 허브 - 글쓰기</h2>

        <Card className="p-2">
          <CardContent className="bg-white">
            <div className="reservation-form-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="reservation-section-title">작성 정보</div>
              <div>
                <label className="block font-semibold mb-1">작성자 (이메일)</label>
                <Input value={author} disabled />
              </div>
              <div>
                <label className="block font-semibold mb-1">회사</label>
                <Input value={companyName || company} disabled />
              </div>
              <div>
                <label className="block font-semibold mb-1">지역</label>
                <Input value={region} disabled />
              </div>

              <div className="reservation-section-title">모집 정보</div>
              <div className="md:col-span-2 lg:col-span-2">
                <label className="block font-semibold mb-1">제목</label>
                <Input
                  value={form.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  disabled={saving}
                />
              </div>

              <div>
                <label className="block font-semibold mb-1">차량 대수</label>
                <Input
                  type="number"
                  inputMode="numeric"
                  value={form.car_count}
                  onChange={(e) => handleChange("car_count", e.target.value)}
                  placeholder="예) 5"
                  disabled={saving}
                />
              </div>

              <div>
                <label className="block font-semibold mb-1">금액(원)</label>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  step={1000}
                  value={form.price}
                  onChange={(e) => handleChange("price", e.target.value)}
                  placeholder="예) 250000"
                  disabled={saving}
                />
              </div>

              <div className="reservation-section-title">일시</div>
              <div>
                <label className="block font-semibold mb-1">시작 일시</label>
                <Input
                  type="datetime-local"
                  step={60}
                  value={form.start_at}
                  onChange={(e) => handleChange("start_at", e.target.value)}
                  disabled={saving}
                />
              </div>
              <div>
                <label className="block font-semibold mb-1">종료 일시</label>
                <Input
                  type="datetime-local"
                  step={60}
                  value={form.end_at}
                  onChange={(e) => handleChange("end_at", e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="reservation-section-title">상세 내용</div>
              <div className="md:col-span-2 lg:col-span-3">
                <label className="block font-semibold mb-1">내용</label>
                <textarea
                  className="w-full border border-gray-300 rounded px-3 py-2 h-32"
                  value={form.content}
                  onChange={(e) => handleChange("content", e.target.value)}
                  disabled={saving}
                  placeholder="내용을 입력하세요"
                />
              </div>

              <div className="reservation-action-row">
                <Button onClick={handleSubmit} disabled={saving}>
                  {saving ? "등록중..." : "등록"}
                </Button>
                <Button variant="outline" onClick={() => navigate("/bus-hub")} disabled={saving}>
                  취소
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
